import React, { useCallback, useMemo, useState } from 'react';
import { Box, Button, Drawer, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import VideoLibraryOutlinedIcon from '@mui/icons-material/VideoLibraryOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import FolderSpecialOutlinedIcon from '@mui/icons-material/FolderSpecialOutlined';
import PolicyOutlinedIcon from '@mui/icons-material/PolicyOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import type { IContest } from '../../../types/global/helper';
import type { IContestManageResponse, TSortBy } from '../../../redux/contest/types';
import type { IPagination } from '../../../hook/usePagination';
import { ROUTES } from '../../../types/global/routes.types';
import { replaceRouteParam } from '../../../utils/routesProcessor';
import { BRAND_LUXE_PRIMARY } from '../brandDashboardTokens';
import { editorialHeadlineSx, editorialLabelSx, midnight } from '../contestWizard/midnightStyles';
import { ContestSubmissionsTab } from '../../../components/ScreenContainer/ContestContainer/Tabs/ContestSubmissionsTab';
import { ContestJurorsTab } from '../../../components/ScreenContainer/ContestContainer/Tabs/ContestJurorsTab';
import { ContestDiscountTab } from '../../../components/ScreenContainer/ContestContainer/Tabs/ContestDiscountsTab';
import { ContestSalesTab } from '../../../components/ScreenContainer/ContestContainer/Tabs/ContestSalesTab';
import { ContestAwardsTab } from '../../../components/ScreenContainer/ContestContainer/Tabs/ContestAwardsTab';
import { ContestAnalyticsTab } from '../../../components/ScreenContainer/ContestContainer/Tabs/ContestAnalyticsTab';
import { ContestPromotionsTab } from '../../../components/ScreenContainer/ContestContainer/Tabs/ContestPromotionsTab';
import { BrandContestDetailsDrawerBody } from './BrandContestDetailsDrawerBody';
import { BrandContestHostsPartnersDrawerBody } from './BrandContestHostsPartnersDrawerBody';
import { BrandContestSetupDrawerBody } from './BrandContestSetupDrawerBody';
import { BrandContestRefundPolicyDrawerBody, resolveContestRefundPolicy } from './BrandContestRefundPolicyDrawerBody';
import {
    BrandCreateSubmissionCategoryModal,
    type SubmissionCategoryModalMode,
} from './BrandCreateSubmissionCategoryModal';
import { ContestFundSummaryBar } from './ContestFundSummaryBar';
import { ResourceTeamPanel } from '../../../components/BrandTeam/ResourceTeamPanel';
import {
    BrandContestSubmissionCategoriesSection,
    submissionCategoryHubSummary,
} from './BrandContestCategoryAwardSections';
import { getAwardPrizeCents } from '../contestWizard/buildAwardCategoryPayload';

export type ManageHubTabId =
    | 'details'
    | 'hosts_partners'
    | 'campaign_setup'
    | 'refund_policy'
    | 'submissions'
    | 'jurors'
    | 'discounts'
    | 'sales'
    | 'awards'
    | 'submission_category'
    | 'analytics'
    | 'promotions'
    | 'team';

export interface BrandContestManageHandlers {
    submissionPagination: IPagination;
    salePagination: IPagination;
    sortOrder: 'asc' | 'desc';
    onSearch: (search: string) => void;
    onSort: (sort: string) => void;
    onSortBy: (sortBy: TSortBy) => void;
    onStartDateChange: (date: string) => void;
    onEndDateChange: (date: string) => void;
    onClearStartDate: () => void;
    onClearEndDate: () => void;
    onSubmissionRowsChange: (rows: number) => void;
    onSubmissionPageChange: (page: number) => void;
    onSaleRowsChange: (rows: number) => void;
    onSalePageChange: (page: number) => void;
    onSortOrderChange: (order: 'asc' | 'desc') => void;
    onExportCSV: (type: 'submissions' | 'awards' | 'sales', data: unknown[]) => void;
    onClearSearch: () => void;
    onClearAllFilters: () => void;
    onSaveChanges: (payload: Record<string, unknown>) => void;
    onProvisionCategories: (categories: Record<string, unknown>[], categoriesEnabled: boolean) => void | Promise<void>;
    onDeleteAwardCategory: (index: number, awardId: string) => void;
    updatingContest?: boolean;
}

interface BrandContestManageHubProps {
    contest: IContest;
    managedContest: IContestManageResponse;
    handlers: BrandContestManageHandlers;
    /** Open contest fund-from-wallet dialog (top up escrow). */
    onAddFunds?: () => void;
    fundingRefreshKey?: number;
}

type CategoryModalState =
    | { open: false }
    | { open: true; mode: SubmissionCategoryModalMode; editCategoryIndex?: number };

export function BrandContestManageHub({
    contest,
    managedContest,
    handlers,
    onAddFunds,
    fundingRefreshKey = 0,
}: BrandContestManageHubProps) {
    const canViewDiscounts = managedContest.access ? managedContest.access.can_view_discounts !== false : true;
    const [openDrawer, setOpenDrawer] = useState<ManageHubTabId | null>(null);
    const [categoryModal, setCategoryModal] = useState<CategoryModalState>({ open: false });

    const closeDrawer = useCallback(() => setOpenDrawer(null), []);

    const openCreateCategoryModal = useCallback(() => {
        setCategoryModal({ open: true, mode: 'create' });
    }, []);

    const openEditCategoryModal = useCallback((editCategoryIndex: number) => {
        setCategoryModal({ open: true, mode: 'edit', editCategoryIndex });
    }, []);

    const closeCategoryModal = useCallback(() => setCategoryModal({ open: false }), []);

    const hasSubmissions = (managedContest.submissions.pagination.totalCount ?? 0) > 0;
    /** Allow fee/category edits on published contests until the first submission lands. */
    const canEditCategories = !hasSubmissions;

    const previewHref = replaceRouteParam(ROUTES.ESVIEW_CONTEST, 'contestId', contest.slug || contest._id);

    const setupExtrasCount = useMemo(() => {
        const refs = contest.contest_reference?.length ?? 0;
        const playlists = contest.playlist?.length ?? 0;
        const attachments = (contest.contest_media ?? []).filter(item => typeof item === 'string').length;
        return refs + playlists + attachments;
    }, [contest.contest_reference, contest.contest_media, contest.playlist]);

    const campaignSetupSummary = useMemo(() => {
        const parts: string[] = [];
        const phasesOn = contest.contest_phases_enabled ?? (contest.contest_phase_dates?.length ?? 0) > 0;
        parts.push(
            phasesOn
                ? `${contest.contest_phase_dates?.length ?? 0} phase${
                      (contest.contest_phase_dates?.length ?? 0) === 1 ? '' : 's'
                  }`
                : 'Phases off',
        );
        parts.push(contest.preselection_enabled ? 'Pre-selection on' : 'Pre-selection off');
        if (setupExtrasCount > 0) {
            parts.push(`${setupExtrasCount} reference${setupExtrasCount === 1 ? '' : 's'}`);
        }
        return parts.join(' · ');
    }, [
        contest.contest_phase_dates?.length,
        contest.contest_phases_enabled,
        contest.preselection_enabled,
        setupExtrasCount,
    ]);

    const hostsPartnersSummary = useMemo(() => {
        const host = contest.host?.name?.trim();
        const partnerCount = contest.partner?.length ?? 0;
        if (host && partnerCount > 0) return `${host} · ${partnerCount} partner${partnerCount !== 1 ? 's' : ''}`;
        if (host) return host;
        if (partnerCount > 0) return `${partnerCount} partner${partnerCount !== 1 ? 's' : ''}`;
        return 'Add host & partners';
    }, [contest.host?.name, contest.partner?.length]);

    const refundPolicySummary = useMemo(() => resolveContestRefundPolicy(contest), [contest]);
    const submissionCategorySummary = useMemo(() => submissionCategoryHubSummary(contest), [contest]);
    /** Refetch fund shortfall whenever prize amounts change after award create/edit/delete. */
    const awardFundingRevision = useMemo(() => {
        const awards = managedContest.award_categories?.categories || [];
        return awards
            .map(a => `${a._id}:${getAwardPrizeCents(a.prize)}`)
            .sort()
            .join('|');
    }, [managedContest.award_categories?.categories]);

    const tiles: {
        id: ManageHubTabId;
        label: string;
        description: string;
        Icon: typeof ArticleOutlinedIcon;
        badge?: string;
    }[] = useMemo(
        () => [
            {
                id: 'details',
                label: 'Details',
                description: 'Identity, narrative, voting, media',
                Icon: ArticleOutlinedIcon,
            },
            {
                id: 'submissions',
                label: 'Submissions',
                description: 'Table & export',
                Icon: VideoLibraryOutlinedIcon,
                badge: String(managedContest.submissions.pagination.totalCount ?? 0),
            },
            {
                id: 'jurors',
                label: 'Jurors',
                description: 'Panel, invites, assignments',
                Icon: GroupsOutlinedIcon,
                badge: String(managedContest.jurors?.length ?? 0),
            },
            {
                id: 'discounts',
                label: 'Discounts',
                description: 'Codes & coupons',
                Icon: LocalOfferOutlinedIcon,
            },
            {
                id: 'sales',
                label: 'Sales',
                description: 'Transactions & export',
                Icon: PaymentsOutlinedIcon,
            },
            {
                id: 'awards',
                label: 'Awards',
                description: 'Categories & laurels',
                Icon: EmojiEventsOutlinedIcon,
            },
            {
                id: 'analytics',
                label: 'Analytics',
                description: 'Performance (coming soon)',
                Icon: BarChartOutlinedIcon,
            },
            {
                id: 'promotions',
                label: 'Promotions',
                description: 'Marketing (coming soon)',
                Icon: CampaignOutlinedIcon,
            },
            {
                id: 'hosts_partners',
                label: 'Hosts & Partners',
                description: hostsPartnersSummary,
                Icon: HandshakeOutlinedIcon,
            },
            {
                id: 'campaign_setup',
                label: 'Campaign Setup',
                description: campaignSetupSummary,
                Icon: FolderSpecialOutlinedIcon,
                badge: setupExtrasCount > 0 ? String(setupExtrasCount) : undefined,
            },
            {
                id: 'refund_policy',
                label: 'Refund Policy',
                description: refundPolicySummary,
                Icon: PolicyOutlinedIcon,
            },
            {
                id: 'submission_category',
                label: 'Submission category',
                description: submissionCategorySummary,
                Icon: CategoryOutlinedIcon,
            },
            {
                id: 'team',
                label: 'Team',
                description: 'Admins, editors, viewers, activity',
                Icon: GroupsOutlinedIcon,
            },
        ],
        [
            campaignSetupSummary,
            hostsPartnersSummary,
            setupExtrasCount,
            refundPolicySummary,
            submissionCategorySummary,
            managedContest.jurors?.length,
            managedContest.submissions.pagination.totalCount,
        ],
    );

    const drawerTitle = tiles.find(t => t.id === openDrawer)?.label ?? '';

    const renderDrawerBody = () => {
        if (!openDrawer) return null;
        const h = handlers;
        const submissions = managedContest.submissions;
        const awards = managedContest.award_categories;
        const sales = managedContest.sales;
        const discountBlock = managedContest.discounts;

        const submissionPagination: IPagination = {
            page: submissions.pagination.currentPage,
            rows: submissions.pagination.pageSize,
            totalCount: submissions.pagination.totalCount,
        };

        switch (openDrawer) {
            case 'details':
                return (
                    <BrandContestDetailsDrawerBody
                        contest={contest}
                        onSaveChanges={h.onSaveChanges}
                        saving={Boolean(h.updatingContest)}
                    />
                );
            case 'hosts_partners':
                return (
                    <BrandContestHostsPartnersDrawerBody
                        contest={contest}
                        onSaveChanges={h.onSaveChanges}
                        saving={Boolean(h.updatingContest)}
                    />
                );
            case 'campaign_setup':
                return (
                    <BrandContestSetupDrawerBody
                        contest={contest}
                        onSaveChanges={h.onSaveChanges}
                        hasSubmissions={hasSubmissions}
                        saving={Boolean(h.updatingContest)}
                    />
                );
            case 'refund_policy':
                return (
                    <BrandContestRefundPolicyDrawerBody
                        contest={contest}
                        onSaveChanges={h.onSaveChanges}
                        hasSubmissions={hasSubmissions}
                        saving={Boolean(h.updatingContest)}
                    />
                );
            case 'submissions':
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box>
                            <Typography
                                sx={{
                                    ...editorialHeadlineSx,
                                    fontSize: { xs: 22, sm: 26 },
                                    color: midnight.onSurface,
                                }}>
                                Campaign Submissions
                            </Typography>
                            <Typography sx={{ ...editorialLabelSx, mt: 0.75, letterSpacing: '0.12em' }}>
                                Search, filter, and export entries — synced with live campaign data
                            </Typography>
                        </Box>
                        {onAddFunds ? (
                            <ContestFundSummaryBar
                                contestId={contest._id}
                                fallbackFundedCents={Number(contest.amount_available) || 0}
                                onAddFunds={onAddFunds}
                                refreshKey={fundingRefreshKey}
                                revisionKey={awardFundingRevision}
                            />
                        ) : null}
                        <ContestSubmissionsTab
                            visualVariant="brand"
                            useParentManageData
                            submissions={submissions.data}
                            pagination={submissionPagination}
                            sortOrder={h.sortOrder}
                            onSearchChange={h.onSearch}
                            onStartDateChange={h.onStartDateChange}
                            onEndDateChange={h.onEndDateChange}
                            onSortOrderChange={h.onSortOrderChange}
                            onRowsChange={h.onSubmissionRowsChange}
                            onPageChange={h.onSubmissionPageChange}
                            onClearSearch={h.onClearSearch}
                            onClearStartDate={h.onClearStartDate}
                            onClearEndDate={h.onClearEndDate}
                            onExportCSV={(rows: unknown[]) => h.onExportCSV('submissions', rows)}
                            onSortByChange={h.onSortBy}
                            contestId={contest._id}
                            contest={contest}
                            awardCategories={awards}
                            preselectionEnabled={contest.preselection_enabled}
                        />
                    </Box>
                );
            case 'jurors':
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <Box>
                            <Typography
                                sx={{
                                    ...editorialHeadlineSx,
                                    fontSize: { xs: 22, sm: 28 },
                                    color: midnight.onSurface,
                                }}>
                                Manage editorial board
                            </Typography>
                            <Typography sx={{ ...editorialLabelSx, mt: 1, maxWidth: 560, lineHeight: 1.5 }}>
                                Invite jurors, track judging progress, and assign submissions across your panel.
                            </Typography>
                        </Box>
                        <ContestJurorsTab
                            visualVariant="brand"
                            jurors={managedContest.jurors || []}
                            contestId={contest._id}
                            preselectionEnabled={contest.preselection_enabled}
                        />
                    </Box>
                );
            case 'discounts':
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                gap: 2,
                                justifyContent: 'space-between',
                                alignItems: { xs: 'flex-start', sm: 'flex-end' },
                            }}>
                            <Box>
                                <Typography
                                    sx={{
                                        ...editorialHeadlineSx,
                                        fontSize: { xs: 26, sm: 34 },
                                        color: midnight.onSurface,
                                    }}>
                                    Discounts
                                </Typography>
                                <Typography
                                    sx={{
                                        ...editorialLabelSx,
                                        mt: 1,
                                        maxWidth: 520,
                                        fontSize: 13,
                                        letterSpacing: '0.06em',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                    }}>
                                    Manage promotional codes and editorial coupons for this campaign.
                                </Typography>
                            </Box>
                        </Box>
                        <ContestDiscountTab
                            contestId={contest._id}
                            discounts={{
                                data: discountBlock.discounts,
                                total: discountBlock.total,
                            }}
                        />
                    </Box>
                );
            case 'sales':
                return (
                    <ContestSalesTab
                        visualVariant="brand"
                        useParentManageData
                        sales={sales}
                        onExportCSV={(rows?: unknown[]) => h.onExportCSV('sales', rows ?? [])}
                    />
                );
            case 'awards':
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {onAddFunds ? (
                            <ContestFundSummaryBar
                                contestId={contest._id}
                                fallbackFundedCents={Number(contest.amount_available) || 0}
                                onAddFunds={onAddFunds}
                                refreshKey={fundingRefreshKey}
                                revisionKey={awardFundingRevision}
                            />
                        ) : null}
                        <ContestAwardsTab
                            visualVariant="brand"
                            awardCategories={awards}
                            contest={contest}
                            hasSubmissions={hasSubmissions}
                        />
                    </Box>
                );
            case 'analytics':
                return <ContestAnalyticsTab visualVariant="brand" />;
            case 'promotions':
                return <ContestPromotionsTab visualVariant="brand" />;
            case 'submission_category':
                return (
                    <BrandContestSubmissionCategoriesSection
                        contest={contest}
                        hasSubmissions={hasSubmissions}
                        onEditCategory={canEditCategories ? openEditCategoryModal : undefined}
                        onCreateCategory={canEditCategories ? openCreateCategoryModal : undefined}
                    />
                );
            case 'team':
                return <ResourceTeamPanel resourceType="contest" resourceId={contest._id} />;
            default:
                return null;
        }
    };

    return (
        <>
            <Box sx={{ mb: 6 }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        justifyContent: 'space-between',
                        gap: 2,
                        mb: 3,
                    }}>
                    <Box>
                        <Typography
                            sx={{
                                ...editorialHeadlineSx,
                                fontSize: { xs: 20, md: 22 },
                                color: midnight.onSurface,
                                mb: 0.5,
                            }}>
                            Manage campaign
                        </Typography>
                        <Typography sx={{ ...editorialLabelSx, mb: 0 }}>
                            Open a workspace — edits sync with your live campaign data
                        </Typography>
                    </Box>
                    <Button
                        component="a"
                        href={previewHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<VisibilityOutlinedIcon />}
                        sx={{
                            flexShrink: 0,
                            px: 3,
                            py: 1.25,
                            borderRadius: 999,
                            textTransform: 'none',
                            fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
                            fontWeight: 800,
                            fontSize: 13,
                            color: '#392700',
                            bgcolor: BRAND_LUXE_PRIMARY,
                            boxShadow: 'none',
                            '&:hover': { bgcolor: '#feb700', boxShadow: 'none' },
                        }}>
                        Preview
                    </Button>
                </Box>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: 'repeat(2, minmax(0, 1fr))',
                            sm: 'repeat(3, minmax(0, 1fr))',
                            md: 'repeat(4, minmax(0, 1fr))',
                        },
                        gap: 2,
                    }}>
                    {tiles.map(tile => {
                        const locked = !canViewDiscounts && tile.id === 'discounts';
                        return (
                            <Box
                                key={tile.id}
                                component="button"
                                type="button"
                                disabled={locked}
                                onClick={() => {
                                    if (locked) return;
                                    setOpenDrawer(tile.id);
                                }}
                                aria-label={locked ? `${tile.label} locked` : tile.label}
                                sx={{
                                    textAlign: 'left',
                                    cursor: locked ? 'not-allowed' : 'pointer',
                                    border: '1px solid rgba(72,72,71,0.25)',
                                    borderRadius: '12px',
                                    bgcolor: midnight.surfaceLow,
                                    p: 2,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    opacity: locked ? 0.55 : 1,
                                    filter: locked ? 'blur(0.3px)' : 'none',
                                    gap: 1,
                                    minHeight: 112,
                                    transition: 'border-color 0.2s, background-color 0.2s',
                                    '&:hover': {
                                        borderColor: 'rgba(255,201,101,0.45)',
                                        bgcolor: midnight.surfaceContainer,
                                    },
                                }}>
                                <Box
                                    sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                    <tile.Icon sx={{ fontSize: 26, color: midnight.primary }} />
                                    {tile.badge !== undefined ? (
                                        <Typography
                                            sx={{
                                                fontSize: 11,
                                                fontWeight: 800,
                                                color: midnight.secondary,
                                                bgcolor: 'rgba(0,253,193,0.12)',
                                                px: 1,
                                                py: 0.25,
                                                borderRadius: 999,
                                            }}>
                                            {tile.badge}
                                        </Typography>
                                    ) : (
                                        <ChevronRightIcon sx={{ fontSize: 18, color: midnight.onSurfaceVariant }} />
                                    )}
                                </Box>
                                <Typography
                                    sx={{
                                        fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
                                        fontWeight: 800,
                                        fontSize: 14,
                                        color: midnight.onSurface,
                                    }}>
                                    {tile.label}
                                </Typography>
                                <Typography sx={{ fontSize: 11, color: midnight.onSurfaceVariant, lineHeight: 1.35 }}>
                                    {locked ? 'Locked' : tile.description}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            </Box>

            <BrandCreateSubmissionCategoryModal
                open={categoryModal.open}
                mode={categoryModal.open ? categoryModal.mode : 'create'}
                contest={contest}
                awardCategories={managedContest.award_categories?.categories}
                editCategoryIndex={categoryModal.open ? categoryModal.editCategoryIndex : 0}
                saving={Boolean(handlers.updatingContest)}
                onClose={closeCategoryModal}
                onSave={async (categories, categoriesEnabled) => {
                    try {
                        await handlers.onProvisionCategories(categories, categoriesEnabled);
                        closeCategoryModal();
                    } catch {
                        /* saga + globalErrorHandler show toast; keep modal open */
                    }
                }}
            />

            <Drawer
                anchor="right"
                open={openDrawer !== null}
                onClose={closeDrawer}
                PaperProps={{
                    sx: {
                        width: { xs: '100%', sm: 'min(50vw, 720px)' },
                        maxWidth: '100%',
                        bgcolor: midnight.surface,
                        backgroundImage: 'none',
                        borderLeft: '1px solid rgba(255,255,255,0.08)',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}>
                <Box
                    sx={{
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2,
                        py: 1.5,
                        borderBottom: '1px solid rgba(72,72,71,0.25)',
                    }}>
                    <Typography sx={{ ...editorialHeadlineSx, fontSize: 18, color: '#ffffff' }}>
                        {drawerTitle}
                    </Typography>
                    <IconButton onClick={closeDrawer} aria-label="Close drawer" sx={{ color: midnight.onSurface }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Box
                    sx={{
                        flex: 1,
                        overflow: 'auto',
                        px: { xs: 1.5, sm: 2 },
                        py: 2,
                        '& .MuiContainer-root': {
                            px: '0 !important',
                            ml: '0 !important',
                            mr: '0 !important',
                            width: '100% !important',
                            maxWidth: '100% !important',
                        },
                    }}>
                    {renderDrawerBody()}
                </Box>
            </Drawer>
        </>
    );
}
